import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { select, Store } from '@ngrx/store';
import { ToastDataV2, ToastV2Service } from '@schenck/rotec-components';
import { forkJoin } from 'rxjs';
import {
  catchError,
  exhaustMap,
  filter,
  finalize,
  map,
  mergeMap,
  switchMap,
  tap,
  withLatestFrom,
} from 'rxjs/operators';
import { TypeDataApi } from 'src/api/type-data.api';
import { ResetStepsForms } from 'src/pages/rotor-type-details/store/rotor-type-details-store.actions';
import { RotorTypeDetailsStore } from 'src/pages/rotor-type-details/store/rotor-type-details-store.basic';
import { routes } from 'src/routes';
import { TypeDataAdapter } from 'src/services/adapters/type-data.adapter';
import { SpinnerService } from 'src/services/spinner/spinner.service';
import {
  DETAILS_NOTIFICATION_POSITION,
  LIST_NOTIFICATION_POSITION,
} from 'src/utils/constants/notification.const';
import {
  PaginatedTypeData,
  RotorTypeErrorResponse,
  TypeData,
} from 'src/utils/interfaces/rotor-type.interfaces';
import * as RotorTypeListActions from './rotor-type-list.actions';
import { GridConfigApi } from '../../../api/gridConfig.api';
import { RotorTypeListState } from './rotor-type-list.reducers';
import { selectRotorType } from './rotor-type-list.selectors';
import { RotorTypeListService } from '../rotor-type-list.service';
import { GeneralEventsService } from 'src/services/general-events.service';
import { TypeDataService } from 'src/services/type-data.service';
import { MachineService } from 'src/services/machine/machine.service';
import { ErrorHandlerService } from 'src/services/error-handler/error-handler.service';
import { first, isArray } from 'lodash';
/**
 * Holds effects for the rotor type list store.
 */
@Injectable()
export class RotorTypeListEffects {
  private readonly actions$ = inject(Actions);
  private readonly typeDataApi = inject(TypeDataApi);
  private readonly gridConfigApi = inject(GridConfigApi);
  private readonly typeDataAdapter = inject(TypeDataAdapter);
  private readonly rotorTypeDetailsStore = inject<Store<RotorTypeDetailsStore>>(Store);
  private readonly rotorTypeListStore = inject<Store<RotorTypeListState>>(Store);
  private readonly router = inject(Router);
  private readonly spinnerService = inject(SpinnerService);
  private readonly toastService = inject(ToastV2Service);
  private readonly rotorTypeListService = inject(RotorTypeListService);
  private readonly generalEventsService = inject(GeneralEventsService);
  private readonly typeDataService = inject(TypeDataService);
  private readonly machineService = inject(MachineService);
  private readonly errorHandlerService = inject(ErrorHandlerService);

  /**
   * Calls request to the BE to save rotor type
   */
  createRotorType$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RotorTypeListActions.createRotorType),
      tap(() => this.spinnerService.setUnitsLoading(true)),
      withLatestFrom(this.rotorTypeListStore.pipe(select(selectRotorType))),
      exhaustMap(
        ([{ formData, units, isEdit, isFinished, shouldShowLoader, preventReload }, rotorType]) => {
          if (shouldShowLoader) {
            this.spinnerService.setLoading(true);
          }

          const request = {
            id: rotorType ? rotorType.data.rotorType.id : null,
            concurrencyToken: rotorType ? rotorType.data.rotorType.concurrencyToken : null,
          };
          const result = isEdit
            ? this.typeDataApi.update$(
                request,
                this.typeDataAdapter.adapt(formData, units, rotorType.data.rotorType.status)
              )
            : this.typeDataApi.create$(this.typeDataAdapter.adapt(formData, units));

          return result.pipe(
            filter((typeData: TypeData) => !!typeData),
            map((typeData: TypeData) => {
              this.showSuccessToast(
                `ROTORNAME has been successfully ${isEdit ? 'updated' : 'created'}`,
                { rotorName: typeData.data.rotorType.name },
                !isFinished
              );

              this.spinnerService.setLoading(false);
              this.spinnerService.setUnitsLoading(false);
              this.typeDataService.getRotorTypeNames$();

              if (!preventReload) {
                if (isFinished) {
                  this.rotorTypeListService.showFirstPage();
                  return RotorTypeListActions.loadTypeData({
                    limit: this.rotorTypeListService.gridConfig.pageSize.toString(),
                  });
                } else {
                  this.rotorTypeDetailsStore.dispatch(new ResetStepsForms());
                  return new ResetStepsForms();
                }
              }
            }),
            tap(() => this.generalEventsService.setRotorTypeCreated(!isEdit)),
            catchError((error: HttpErrorResponse) => {
              this.handleCreateRotorTypeError(error);
              throw error;
            }),
            finalize(() => {
              this.router.navigateByUrl(
                isFinished
                  ? routes.rotorTypeList.absoluteUrl
                  : routes.rotorTypeDetailsCreate.absoluteUrl
              );
            })
          );
        }
      )
    )
  );

  private handleCreateRotorTypeError(error: HttpErrorResponse) {
    let err: RotorTypeErrorResponse = {
      message: error?.message,
      params: { ['error']: [error.error] },
    };

    this.spinnerService.setLoading(false);
    this.spinnerService.setUnitsLoading(false);

    if (
      error.status === 409 &&
      error.message.indexOf('The rotor type') !== 0 &&
      error.message.indexOf('already exists') !== 0
    ) {
      this.showErrorToast(
        {
          message: 'Rotor type name already exists. Please choose another name.',
        },
        DETAILS_NOTIFICATION_POSITION
      );
      this.typeDataService.setUniqueNameError(true);
    } else {
      if (
        error.error?.params &&
        typeof error.error.params === 'object' &&
        isArray(first(Object.values(error.error.params)))
      ) {
        this.showErrorToast(
          {
            message: first(first(Object.values(error.error.params as Record<string, string[]>))),
          },
          DETAILS_NOTIFICATION_POSITION
        );
      } else {
        this.showErrorToast(err, DETAILS_NOTIFICATION_POSITION);
      }
    }
  }

  /**
   * Calls request to the BE to duplicate rotor type
   */
  duplicateRotorType$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RotorTypeListActions.duplicateRotorType),
      tap(() => this.spinnerService.setGridLoading(true)),
      switchMap((rotorType) => {
        return this.typeDataApi.duplicate$(rotorType.id, rotorType.name).pipe(
          filter((typeData: TypeData) => !!typeData),
          map((typeData: TypeData) => {
            this.generalEventsService.setRotorTypeCreated(true);
            this.rotorTypeListService.showFirstPage();
            this.showSuccessToast(`ROTORNAME has been successfully created`, {
              rotorName: typeData.data.rotorType.name,
            });

            return RotorTypeListActions.loadTypeData({
              limit: this.rotorTypeListService.gridConfig.pageSize.toString(),
            });
          }),
          catchError((error: HttpErrorResponse) => {
            this.spinnerService.setGridLoading(false);
            this.handleCreateRotorTypeError(error);

            throw Error(error.message);
          })
        );
      })
    )
  );

  /**
   * Calls request to the BE to load available type data
   *
   * We need to make sure, we load all machines first (not only available machines)
   * Otherwise, filters will not work, and the page will not load.
   * @see TypeDataApi::createFilterURL
   */
  loadTypeData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RotorTypeListActions.loadTypeData),
      tap(() => this.spinnerService.setGridLoading(true)),
      switchMap((action) =>
        this.machineService.machines$.pipe(
          switchMap((_machines) => {
            return this.typeDataApi
              .get$(
                action.page,
                action.limit,
                action.filter && this.typeDataService.createFilterURL(action.filter)
              )
              .pipe(
                map((typeData: PaginatedTypeData) =>
                  RotorTypeListActions.saveTypeData({ typeData })
                ),
                catchError(({ error }: HttpErrorResponse) => {
                  this.showErrorToast(error, LIST_NOTIFICATION_POSITION);
                  this.errorHandlerService.setAppError(true);
                  throw Error(error.message);
                }),
                finalize(() => this.spinnerService.setGridLoading(false))
              );
          })
        )
      )
    )
  );

  /**
   * Calls request to the BE to load selected type data
   */
  loadSelectedTypeData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RotorTypeListActions.loadSelectedTypeData),
      switchMap(({ id }) => {
        return this.typeDataApi.getById$(id).pipe(
          map((rotorType: TypeData) => RotorTypeListActions.saveRotorType({ rotorType })),
          catchError(({ error }: HttpErrorResponse) => {
            this.router.navigate([routes.rotorTypeList.url]);
            this.toastService.open(<ToastDataV2>{
              type: 'error',
              title: 'Error',
              text: error?.message,
              position: LIST_NOTIFICATION_POSITION,
            });
            throw Error(error?.message);
          })
        );
      })
    )
  );

  /**
   * Calls request to the BE to delete selected rotor types
   */
  deleteRotorTypes$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RotorTypeListActions.deleteRotorTypes),
      tap(() => this.spinnerService.setGridLoading(true)),
      switchMap(({ rotorTypes }) => {
        const requests = rotorTypes.map((rotorType) => this.typeDataApi.delete$(rotorType));

        return forkJoin(requests).pipe(
          map(() =>
            RotorTypeListActions.loadTypeData({
              page:
                rotorTypes.length < this.rotorTypeListService.state.results.length
                  ? (this.rotorTypeListService.state.page + 1).toString() // stays on the same page
                  : this.rotorTypeListService.state.page.toString(), // go to the previous page
              filter: this.rotorTypeListService.gridFilter,
              limit: this.rotorTypeListService.gridConfig.pageSize.toString(),
            })
          ),
          tap(() => {
            if (rotorTypes.length > 1) {
              this.showSuccessToast('Rotor types have been deleted');
            } else {
              this.showSuccessToast('ROTORNAME has been deleted', {
                rotorName: rotorTypes[0].name,
              });
            }
            this.typeDataService.getRotorTypeNames$();
          }),
          catchError(({ error }: HttpErrorResponse) => {
            this.spinnerService.setGridLoading(false);
            this.showErrorToast(error, LIST_NOTIFICATION_POSITION);
            throw Error(error.message);
          })
        );
      })
    )
  );
  /**
   * Load grid configuration from BE, save it to the store
   */
  loadGridConfig$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RotorTypeListActions.loadGridConfig),
      tap(() => this.spinnerService.setGridLoading(true)),
      switchMap(() => {
        return this.gridConfigApi
          .get$()
          .pipe(map((gridConfig) => RotorTypeListActions.saveGridConfig(gridConfig)));
      })
    )
  );

  /**
   * Calls request to the BE to save grid configuration
   */
  saveGridConfig$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RotorTypeListActions.updateGridConfig),
      mergeMap((gridConfig) => {
        const result = this.gridConfigApi.save$(gridConfig);

        return result.pipe(
          map(() => {
            return RotorTypeListActions.saveGridConfig(gridConfig);
          }),
          catchError(({ error }: HttpErrorResponse) => {
            throw Error(error.message);
          })
        );
      })
    )
  );

  private showSuccessToast(
    text: string,
    textTranslateParams?: Record<string, any>,
    isDetails?: boolean
  ): void {
    this.toastService.open(<ToastDataV2>{
      text,
      type: 'success',
      title: 'Success',
      textTranslateParams,
      position: isDetails ? DETAILS_NOTIFICATION_POSITION : LIST_NOTIFICATION_POSITION,
    });
  }

  private showErrorToast(error: RotorTypeErrorResponse, toastPosition: string): void {
    let text, position;

    if (error.params) {
      text = Object.values(error?.params)
        .map(([item]: string[]) => item)
        .join('\n');
      position = `calc(${toastPosition} - ${16 * (Object.values(error?.params).length - 1)}px)`;
    } else if (error.message) {
      text = error.message;
    }

    this.toastService.open(<ToastDataV2>{
      text,
      position,
      type: 'error',
      title: 'Error',
    });
  }
}
